import React from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../../../../../../components/ui/button';
import { Input } from '../../../../../../components/ui/input';
import { COATING_DFT_CLOCKS, COATING_DFT_SUB } from './coatingChecklistMockData';

/** Stable key for one DFT cell (one row per layer, clock × Left/Center/Right). */
export function dftLayerCellKey(layerId: string, clock: string, sub: string) {
  return `dft::${layerId}::${clock}::${sub}`;
}

export interface CoatingDftLayerRow {
  id: string;
  name: string;
}

export function CoatingDftLayersTable({
  layers,
  values,
  onChange,
  onLayerNameChange,
  onAddLayer,
  onRemoveLayer,
}: {
  layers: CoatingDftLayerRow[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onLayerNameChange: (layerId: string, name: string) => void;
  onAddLayer: () => void;
  onRemoveLayer: (layerId: string) => void;
}) {
  const canRemove = layers.length > 1;

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded border border-slate-300 bg-white">
        <table className="w-full min-w-[640px] border-collapse text-xs">
          <caption className="caption-top border-b border-slate-200 bg-slate-100 px-2 py-2 text-left text-xs font-semibold text-slate-900">
            Dry film thickness
          </caption>
          <thead>
            <tr>
              <th
                rowSpan={2}
                className="border border-slate-300 bg-slate-200 px-2 py-2 text-left align-middle font-semibold text-slate-900 w-[min(220px,28vw)]"
              >
                Layer
              </th>
              {COATING_DFT_CLOCKS.map((clock) => (
                <th
                  key={clock}
                  colSpan={COATING_DFT_SUB.length}
                  className="border border-slate-300 bg-slate-100 px-1 py-1.5 text-center font-semibold"
                >
                  {clock}
                </th>
              ))}
            </tr>
            <tr>
              {COATING_DFT_CLOCKS.map((clock) =>
                COATING_DFT_SUB.map((sub) => (
                  <th
                    key={`${clock}-${sub}`}
                    className="border border-slate-300 bg-slate-50 px-0.5 py-1 text-[10px] font-medium text-slate-600"
                  >
                    {sub}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {layers.map((layer) => (
              <tr key={layer.id} className="hover:bg-slate-50/60">
                <td className="border border-slate-300 bg-slate-50/50 p-1">
                  <div className="flex items-center gap-1">
                    <Input
                      value={layer.name}
                      onChange={(e) => onLayerNameChange(layer.id, e.target.value)}
                      className="h-8 min-w-0 flex-1 rounded border-slate-200 text-[11px] focus-visible:ring-1"
                      aria-label="Layer name"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0 text-slate-500 hover:text-destructive"
                      disabled={!canRemove}
                      onClick={() => onRemoveLayer(layer.id)}
                      aria-label={`Remove layer ${layer.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
                {COATING_DFT_CLOCKS.map((clock) =>
                  COATING_DFT_SUB.map((sub) => {
                    const k = dftLayerCellKey(layer.id, clock, sub);
                    return (
                      <td key={k} className="border border-slate-300 p-0">
                        <Input
                          value={values[k] ?? ''}
                          onChange={(e) => onChange(k, e.target.value)}
                          className="h-8 rounded-none border-0 text-center text-[11px] focus-visible:ring-1"
                          aria-label={`${layer.name} ${clock} ${sub}`}
                        />
                      </td>
                    );
                  })
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button type="button" variant="outline" size="sm" className="text-xs" onClick={onAddLayer}>
        Add layer
      </Button>
    </div>
  );
}
